import { useEffect, useState } from "react";
import BASE_URL, { apiFetch } from "api/Api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "./letter.css";

const LeaderLetterGenerate = () => {
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");

  const [allFields, setAllFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [templateContent, setTemplateContent] = useState("");
  const [finalLetter, setFinalLetter] = useState("");

  const [noTemplateMessage, setNoTemplateMessage] = useState("");
  const [noFieldMessage, setNoFieldMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    loadCategories();
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  // Dynamic Live Preview updates as user types into form
  useEffect(() => {
    if (!templateContent) {
      setFinalLetter("");
      return;
    }

    let preview = templateContent;

    // Fill date if present
    const formattedDate = new Date().toLocaleDateString("mr-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    preview = preview.replace(/{{\s*date\s*}}/g, formattedDate);

    // Leader info replacements if present in template
    if (user) {
      preview = preview
        .replace(/{{\s*full_name\s*}}/g, user.full_name || "")
        .replace(/{{\s*mobile\s*}}/g, user.mobile || "")
        .replace(/{{\s*ward_number\s*}}/g, user.ward_number || "")
        .replace(/{{\s*ward_name\s*}}/g, user.ward_name || "");
    }

    // Replace form fields dynamically
    allFields.forEach((field) => {
      const key = field.field_key;
      const value = formData[key];

      const regex = new RegExp(`{{\\s*${key}\\s*}}`, "g");
      if (value !== undefined && value !== null && value !== "") {
        preview = preview.replace(regex, value);
      } else {
        // Show subtle visual badge placeholder during live typing
        preview = preview.replace(
          regex,
          `<span class="placeholder-tag">[${field.field_label || key}]</span>`
        );
      }
    });

    // Replace any remaining unfulfilled placeholders
    preview = preview.replace(
      /{{\s*(.*?)\s*}}/g,
      '<span class="placeholder-tag">[$1]</span>'
    );

    const formattedLetter = preview
      .replace(/\n/g, "<br/>")
      .replace(/  /g, "&nbsp;&nbsp;");

    setFinalLetter(formattedLetter);
  }, [formData, templateContent, allFields, user]);

  const loadCategories = async () => {
    try {
      const data = await apiFetch("super_admin/list_help_category");
      setCategories(data.categories || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCategoryChange = async (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedTemplate("");
    setAllFields([]);
    setFormData({});
    setTemplateContent("");
    setFinalLetter("");
    setNoTemplateMessage("");
    setNoFieldMessage("");

    if (!categoryId) return;

    try {
      const data = await apiFetch(`leader_admin/get_template/${categoryId}`);

      if (data.template && data.template.length > 0) {
        setTemplates(data.template);
      } else {
        setTemplates([]);
        setNoTemplateMessage("या श्रेणीसाठी कोणताही नमुना उपलब्ध नाही.");
      }
    } catch (error) {
      setTemplates([]);
      setNoTemplateMessage("या श्रेणीसाठी कोणताही नमुना उपलब्ध नाही.");
    }
  };

  const handleTemplateChange = async (templateId) => {
    setSelectedTemplate(templateId);
    setAllFields([]);
    setFormData({});
    setTemplateContent("");
    setFinalLetter("");
    setNoFieldMessage("");

    if (!templateId) return;

    try {
      const data = await apiFetch(`leader_admin/get_template_by_id/${templateId}`);

      const templateFields = data.template_fields || [];
      const categoryFields = data.category_fields || [];
      const mergedFields = [...categoryFields, ...templateFields];

      setTemplateContent(data.template?.template_content || "");
      setAllFields(mergedFields);

      if (mergedFields.length === 0) {
        setNoFieldMessage("या नमुन्यासाठी कोणतीही अतिरिक्त माहिती भरणे आवश्यक नाही.");
      }
    } catch (error) {
      console.error(error);
      setAllFields([]);
      setNoFieldMessage("या नमुन्यासाठी माहिती फील्ड्स मिळवण्यात अडचण आली.");
    }
  };

  const handleInputChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleGenerate = async (e) => {
    e.preventDefault();

    for (const field of allFields) {
      if (field.is_required && !formData[field.field_key]) {
        alert(`कृपया ${field.field_label} माहिती प्रविष्ट करा.`);
        return;
      }
    }

    setLoading(true);

    try {
      const data = await apiFetch("leader_admin/generate_letter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          template_id: selectedTemplate,
          form_data: formData,
        }),
      });

      if (data.final_letter) {
        setTemplateContent(data.final_letter);
      }
    } catch (error) {
      alert(error.message || "पत्र तयार करताना अडचण आली.");
    } finally {
      setLoading(false);
    }
  };

  // High Resolution HTML-to-PDF Client Side Generation (Matching Live Preview exactly)
  const handleDownloadPDF = async () => {
    const printElement = document.getElementById("print-section");
    if (!printElement) {
      alert("कृपया आधी श्रेणी आणि पत्राचा नमुना निवडा.");
      return;
    }

    setDownloading(true);

    try {
      // Temporarily hide placeholder tags for clean official PDF output
      const placeholders = printElement.querySelectorAll(".placeholder-tag");
      placeholders.forEach((el) => (el.style.visibility = "hidden"));

      const canvas = await html2canvas(printElement, {
        scale: 2, // High DPI canvas rendering for sharp Devanagari text
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        windowWidth: printElement.scrollWidth,
      });

      // Restore placeholders
      placeholders.forEach((el) => (el.style.visibility = "visible"));

      const imgData = canvas.toDataURL("image/jpeg", 0.98);
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pdfWidth = pdf.internal.pageSize.getWidth(); // 210mm
      const pdfHeight = pdf.internal.pageSize.getHeight(); // 297mm

      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      // Fit within single A4 page
      const finalImgHeight = Math.min(imgHeight, pdfHeight);

      pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, finalImgHeight);
      pdf.save(`Letter_${selectedTemplate || "Document"}_${Date.now()}.pdf`);
    } catch (error) {
      console.error("PDF Download Error:", error);
      alert("PDF डाउनलोड करताना त्रुटी आली: " + error.message);
    } finally {
      setDownloading(false);
    }
  };

  // Print Handler
  const handlePrint = () => {
    const printContent = document.getElementById("print-section");
    if (!printContent) return;

    const printWindow = window.open("", "_blank", "width=950,height=850");

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>पत्र प्रिंट (Letter Print)</title>
          <link rel="preconnect" href="https://fonts.googleapis.com">
          <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
          <link href="https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;500;600;700&display=swap" rel="stylesheet">
          <style>
            @page {
              size: A4;
              margin: 12mm 15mm;
            }
            body {
              font-family: 'Noto Sans Devanagari', sans-serif;
              margin: 0;
              padding: 0;
              font-size: 14px;
              line-height: 1.65;
              color: #000;
              -webkit-print-color-adjust: exact;
            }
            .letter-paper {
              width: 100% !important;
              box-shadow: none !important;
              border: none !important;
              padding: 0 !important;
              margin: 0 !important;
            }
            .placeholder-tag {
              display: none !important;
            }
            p {
              margin: 5px 0;
            }
            .letter-header {
              border-bottom: 2.5px double #002b5b;
              padding-bottom: 10px;
              margin-bottom: 14px;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .letter-header-title {
              color: #002b5b;
              font-weight: 700;
              font-size: 1.25rem;
              margin: 0;
            }
            .letter-meta-bar {
              display: flex;
              justify-content: space-between;
              font-weight: 600;
              margin-bottom: 16px;
              padding-bottom: 6px;
              border-bottom: 1px dashed #ccc;
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 600);
  };

  const formattedCurrentDate = new Date().toLocaleDateString("mr-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div className="container-fluid mt-4" style={{ fontSize: "0.9rem" }}>
        {/* HEADER BANNER */}
        <div
          className="p-4 rounded-4 shadow-lg mb-4 text-white d-flex justify-content-between align-items-center flex-wrap gap-3"
          style={{ background: "linear-gradient(135deg, #002B5B 0%, #007BFF 100%)" }}
        >
          <div>
            <h4 className="fw-bold mb-1" style={{ fontSize: "1.3rem" }}>
              <Icon className="align-middle me-2">article</Icon>
              अधिकृत पत्र निर्मिती प्रणाली (Letter Generation)
            </h4>
            <p className="mb-0 text-white-50" style={{ fontSize: "0.85rem" }}>
              योग्य श्रेणी व नमुना निवडून आवश्यक माहिती भरा व त्वरित अधिकृत पत्र डाउनलोड करा.
            </p>
          </div>
        </div>

        <div className="row g-4">
          {/* FORM SECTION (LEFT COLUMN) */}
          <div className="col-lg-5 col-md-12">
            <div className="card shadow-sm rounded-4 border-0 mb-4">
              <div className="card-header bg-white border-0 pt-4 px-4 pb-0">
                <h6 className="fw-bold text-dark mb-0">
                  <Icon className="align-middle me-2 text-primary">edit_note</Icon>
                  पत्र माहिती प्रविष्ट करा
                </h6>
              </div>
              <div className="card-body p-4">
                <form onSubmit={handleGenerate}>
                  {/* CATEGORY SELECTOR */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      १. श्रेणी निवडा (Select Category) <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select form-select-sm py-2 rounded-3 shadow-none border-secondary-subtle"
                      value={selectedCategory}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                    >
                      <option value="">-- श्रेणी निवडा --</option>
                      {categories.map((cat) => (
                        <option key={cat.category_id} value={cat.category_id}>
                          {cat.category_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* TEMPLATE SELECTOR */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold text-secondary small mb-1">
                      २. पत्राचा नमुना निवडा (Select Template) <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select form-select-sm py-2 rounded-3 shadow-none border-secondary-subtle"
                      value={selectedTemplate}
                      onChange={(e) => handleTemplateChange(e.target.value)}
                      disabled={!templates.length}
                    >
                      <option value="">-- नमुना निवडा --</option>
                      {templates.map((temp) => (
                        <option key={temp.template_id} value={temp.template_id}>
                          {temp.template_title}
                        </option>
                      ))}
                    </select>
                    {noTemplateMessage && (
                      <div className="text-warning small mt-1 fw-semibold">
                        <Icon className="align-middle me-1" style={{ fontSize: "1rem" }}>
                          info
                        </Icon>
                        {noTemplateMessage}
                      </div>
                    )}
                  </div>

                  <hr className="my-3 text-secondary opacity-25" />

                  {/* DYNAMIC FORM FIELDS */}
                  {allFields.length > 0 && (
                    <div className="mb-3">
                      <h6 className="fw-semibold text-secondary small mb-3">
                        ३. आवश्यक तपशील भरा:
                      </h6>

                      {allFields.map((field, index) => (
                        <div key={index} className="mb-3">
                          <label className="form-label fw-medium text-dark small mb-1">
                            {field.field_label}
                            {field.is_required && <span className="text-danger"> *</span>}
                          </label>

                          {field.field_type === "textarea" ? (
                            <textarea
                              className="form-control form-control-sm rounded-3 shadow-none border-secondary-subtle"
                              rows="3"
                              placeholder={`${field.field_label} प्रविष्ट करा...`}
                              value={formData[field.field_key] || ""}
                              onChange={(e) =>
                                handleInputChange(field.field_key, e.target.value)
                              }
                            />
                          ) : (
                            <input
                              type={field.field_type || "text"}
                              className="form-control form-control-sm rounded-3 shadow-none border-secondary-subtle py-2"
                              placeholder={`${field.field_label} प्रविष्ट करा...`}
                              value={formData[field.field_key] || ""}
                              onChange={(e) =>
                                handleInputChange(field.field_key, e.target.value)
                              }
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {noFieldMessage && (
                    <div className="text-muted small my-3">
                      <Icon className="align-middle me-1" style={{ fontSize: "1rem" }}>
                        check_circle
                      </Icon>
                      {noFieldMessage}
                    </div>
                  )}

                  {allFields.length > 0 && (
                    <button
                      className="btn btn-primary w-100 py-2 rounded-3 text-white fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2"
                      style={{ fontSize: "0.88rem" }}
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <CircularProgress size={16} color="inherit" />
                          पत्र अपडेट होत आहे...
                        </>
                      ) : (
                        <>
                          <Icon fontSize="small">autorenew</Icon>
                          पत्र अद्यतनित करा
                        </>
                      )}
                    </button>
                  )}
                </form>
              </div>
            </div>
          </div>

          {/* LIVE LETTER PREVIEW SECTION (RIGHT COLUMN) */}
          <div className="col-lg-7 col-md-12">
            <div className="card shadow-sm rounded-4 border-0 overflow-hidden mb-4">
              <div className="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2">
                  <Icon className="text-primary">visibility</Icon>
                  <span className="fw-bold text-dark" style={{ fontSize: "0.95rem" }}>
                    पत्र पूर्वावलोकन (Live A4 Preview)
                  </span>
                </div>

                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-secondary btn-sm px-3 rounded-3 d-flex align-items-center gap-1"
                    style={{ fontSize: "0.82rem" }}
                    onClick={handlePrint}
                    disabled={!finalLetter}
                  >
                    <Icon style={{ fontSize: "1.1rem" }}>print</Icon>
                    प्रिंट करा
                  </button>

                  <button
                    className="btn btn-success btn-sm px-3 rounded-3 text-white fw-bold d-flex align-items-center gap-1 shadow-sm"
                    style={{ fontSize: "0.82rem" }}
                    onClick={handleDownloadPDF}
                    disabled={downloading || !selectedTemplate}
                  >
                    {downloading ? (
                      <>
                        <CircularProgress size={14} color="inherit" />
                        डाउनलोड होत आहे...
                      </>
                    ) : (
                      <>
                        <Icon style={{ fontSize: "1.1rem" }}>picture_as_pdf</Icon>
                        PDF डाउनलोड करा
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="preview-wrapper">
                {finalLetter ? (
                  <div id="print-section" className="letter-paper">
                    {/* ELEGANT OFFICIAL LETTERHEAD HEADER */}
                    <div className="letter-header">
                      <div className="letter-header-left">
                        {user?.party_symbol ? (
                          <img
                            src={`${BASE_URL}${user.party_symbol}`}
                            alt="Party Emblem"
                            className="letter-logo"
                          />
                        ) : (
                          <div
                            className="letter-logo d-flex align-items-center justify-content-center bg-light text-primary fw-bold"
                            style={{ fontSize: "1.2rem" }}
                          >
                            <Icon>account_balance</Icon>
                          </div>
                        )}
                        <div>
                          <h3 className="letter-header-title">
                            {user?.full_name ? `मा. ${user.full_name}` : "कार्यालय: लोकप्रतिनिधी"}
                          </h3>
                          <p className="letter-header-subtitle">
                            {user?.ward_name
                              ? `प्रभाग क्र. ${user.ward_number || ""}, ${user.ward_name}`
                              : "नगरसेवक / लोकप्रतिनिधी"}
                          </p>
                        </div>
                      </div>

                      <div className="letter-header-right">
                        <div>
                          <strong>संपर्क:</strong> {user?.mobile || "---"}
                        </div>
                        {user?.email && (
                          <div>
                            <strong>ईमेल:</strong> {user.email}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* REFERENCE & DATE META BAR */}
                    <div className="letter-meta-bar">
                      <div>
                        <span>जावक क्र.: </span>
                        <span className="fw-normal">
                          {formData.outward_no || "___________________"}
                        </span>
                      </div>
                      <div>
                        <span>दिनांक: </span>
                        <span className="fw-normal">{formattedCurrentDate}</span>
                      </div>
                    </div>

                    {/* LETTER BODY CONTENT */}
                    <div
                      className="letter-content"
                      dangerouslySetInnerHTML={{ __html: finalLetter }}
                    />

                    {/* SIGNATURE BLOCK */}
                    <div className="letter-signature">
                      <div className="letter-signature-box">
                        <div className="letter-signature-title">आपला / आपली नम्र,</div>
                        <div className="letter-signature-name">
                          {user?.full_name || "स्वाक्षरी"}
                        </div>
                        <div className="letter-signature-sub">
                          {user?.ward_name
                            ? `नगरसेवक, प्रभाग क्र. ${user.ward_number || ""}`
                            : "लोकप्रतिनिधी"}
                        </div>
                      </div>
                    </div>

                    {/* FOOTER ACCENT LINE */}
                    <div className="letter-footer-accent">
                      कार्यालय पत्ता: प्रभाग कार्यालय, नागरिक जनसंपर्क कक्ष.
                    </div>
                  </div>
                ) : (
                  <div className="d-flex flex-column align-items-center justify-content-center text-center p-5 my-auto text-muted">
                    <Icon style={{ fontSize: "3.5rem", opacity: 0.3 }} className="mb-2">
                      description
                    </Icon>
                    <h6 className="fw-bold mb-1">पूर्वावलोकन उपलब्ध नाही</h6>
                    <p className="small text-secondary mb-0" style={{ maxWidth: "320px" }}>
                      कृपया डाव्या बाजूला श्रेणी आणि पत्राचा नमुना निवडा. पत्राचे लाईव्ह पूर्वावलोकन येथे दिसेल.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default LeaderLetterGenerate;